import { FailedAutopayRecoveryPage } from '@/pages/FailedAutopayRecoveryPage'
import { FinanceOverviewPage } from '@/pages/FinanceOverviewPage'
import { FinancialHistoryPage } from '@/pages/FinancialHistoryPage'
import { AgingReportPage, type AgingContactDrillDown } from '@/pages/AgingReportPage'
import { CollectionsReportPage } from '@/pages/CollectionsReportPage'
import { MembershipReportPage } from '@/pages/MembershipReportPage'
import { InvoiceDetailPage, InvoicesPage, type Invoice } from '@/pages/InvoicesPage'
import { IncomeReportPage } from '@/pages/IncomeReportPage'
import type { IncomeInvoiceDrillDown } from '@/lib/incomeInvoiceDrillDown'
import { PayoutsPanel } from '@/pages/PayoutsPanel'
import { RecognitionSchedulePage } from '@/pages/RecognitionSchedulePage'
import { TaxReportPage } from '@/pages/TaxReportPage'
import { UnreachableMembersPage } from '@/pages/UnreachableMembersPage'
import { DonationsPage } from '@/pages/DonationsPage'
import { CampaignDetailPage } from '@/pages/CampaignDetailPage'
import { AdminCampaignsPage } from '@/pages/AdminCampaignsPage'
import { AdminCampaignDetailPage } from '@/pages/AdminCampaignDetailPage'

export type FinanceView =
  | { kind: 'finance-overview' }
  | { kind: 'financial-history' }
  | {
      kind: 'finance-invoices'
      drillDown?: IncomeInvoiceDrillDown
      agingContact?: AgingContactDrillDown
    }
  | { kind: 'finance-autopay-recovery' }
  | { kind: 'finance-tax-report' }
  | { kind: 'finance-income-report' }
  | { kind: 'finance-collections-report' }
  | { kind: 'finance-membership-report' }
  | { kind: 'finance-aging-report' }
  | { kind: 'finance-recognition-schedule' }
  | { kind: 'finance-payouts' }
  | { kind: 'finance-unreachable-members' }
  | { kind: 'finance-donations' }
  | { kind: 'finance-campaigns' }
  | { kind: 'finance-campaign-detail'; campaignId: number }
  | { kind: 'finance-admin-campaign-detail'; campaignId: number }
  | { kind: 'invoice-detail'; invoiceId: number }

export function isFinanceView(view: { kind: string }): view is FinanceView {
  return [
    'finance-overview',
    'financial-history',
    'finance-invoices',
    'finance-autopay-recovery',
    'finance-tax-report',
    'finance-income-report',
    'finance-collections-report',
    'finance-membership-report',
    'finance-aging-report',
    'finance-recognition-schedule',
    'finance-payouts',
    'finance-unreachable-members',
    'finance-donations',
    'finance-campaigns',
    'finance-campaign-detail',
    'finance-admin-campaign-detail',
    'invoice-detail',
  ].includes(view.kind)
}

interface StaffFinanceViewProps {
  view: FinanceView
  currentAssociationDate?: string
  onOpenInvoice: (invoice: Invoice) => void
  onOpenInvoiceById: (invoiceId: number) => void
  onBackToInvoices: () => void
  onOpenMember: (contactId: number) => void
  onIncomeDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
  onAgingDrillDown: (drillDown: AgingContactDrillDown) => void
  onOpenCampaign?: (campaignId: number) => void
}

export function StaffFinanceView(props: StaffFinanceViewProps) {
  if (props.view.kind === 'finance-donations') {
    return (
      <DonationsPage
        currentAssociationDate={props.currentAssociationDate}
        onOpenCampaign={props.onOpenCampaign}
      />
    )
  }
  if (props.view.kind === 'finance-campaigns') {
    return <AdminCampaignsPage />
  }
  if (props.view.kind === 'finance-admin-campaign-detail') {
    return (
      <AdminCampaignDetailPage
        campaign={{
          id: props.view.campaignId,
          name: '',
          goal: null,
          goal_cents: null,
          open_on: null,
          close_on: null,
          archived: false,
        }}
        onBack={() => window.location.assign('/finance/donations/campaigns')}
      />
    )
  }
  if (props.view.kind === 'finance-campaign-detail') {
    return (
      <CampaignDetailPage campaignId={props.view.campaignId} onBack={() => window.history.back()} />
    )
  }
  if (props.view.kind === 'finance-membership-report') return <MembershipReportPage />

  return <EstablishedStaffFinanceView {...props} view={props.view} />
}

function EstablishedStaffFinanceView({
  view,
  currentAssociationDate,
  onOpenInvoice,
  onOpenInvoiceById,
  onBackToInvoices,
  onOpenMember,
  onIncomeDrillDown,
  onAgingDrillDown,
}: StaffFinanceViewProps) {
  switch (view.kind) {
    case 'finance-overview':
      return <FinanceOverviewPage />
    case 'financial-history':
      return <FinancialHistoryPage />
    case 'finance-invoices':
      return (
        <InvoicesPage
          currentAssociationDate={currentAssociationDate}
          onOpenInvoice={onOpenInvoice}
          drillDown={view.drillDown}
          agingContact={view.agingContact}
        />
      )
    case 'finance-autopay-recovery':
      return <FailedAutopayRecoveryPage onOpenInvoice={onOpenInvoiceById} />
    case 'finance-tax-report':
      return <TaxReportPage currentAssociationDate={currentAssociationDate} />
    case 'finance-income-report':
      return <IncomeReportPage onOpenInvoice={onOpenInvoiceById} onDrillDown={onIncomeDrillDown} />
    case 'finance-collections-report':
      return <CollectionsReportPage />
    case 'finance-aging-report':
      return <AgingReportPage onDrillDown={onAgingDrillDown} />
    case 'finance-recognition-schedule':
      return (
        <RecognitionSchedulePage
          currentAssociationDate={currentAssociationDate ?? new Date().toISOString().slice(0, 10)}
          onOpenInvoice={onOpenInvoiceById}
        />
      )
    case 'finance-payouts':
      return <PayoutsPanel />
    case 'finance-unreachable-members':
      return <UnreachableMembersPage onOpenMember={onOpenMember} />
    case 'invoice-detail':
      return (
        <InvoiceDetailPage
          invoiceId={view.invoiceId}
          currentAssociationDate={currentAssociationDate}
          onBack={onBackToInvoices}
        />
      )
  }
}
